import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { usePersonProfiles, CreatePersonData } from '@/hooks/usePersonProfiles';
import { useFamily } from '@/hooks/useFamily';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useCurrentSection } from '@/hooks/useCurrentSection';
import { PageHeader } from '@/components/PageHeader';

export default function EditPerson() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { family } = useFamily();
  const { persons, updatePerson, loading } = usePersonProfiles(family?.id);
  const { primaryVariant } = useCurrentSection();
  
  const [formData, setFormData] = useState<CreatePersonData>({
    first_name: '',
    last_name: '',
    category: 'friends',
    date_of_birth: '',
    gender: undefined,
    phone_primary: '',
    email: '',
    address: '',
    notes: '',
    emergency_contact: false,
    family_role: undefined
  });

  const person = persons?.find(p => p.id === id);

  useEffect(() => {
    if (person) {
      setFormData({
        first_name: person.first_name,
        last_name: person.last_name,
        category: person.category,
        date_of_birth: person.date_of_birth || '',
        gender: person.gender,
        phone_primary: person.phone_primary || '',
        email: person.email || '',
        address: person.address || '',
        notes: person.notes || '',
        emergency_contact: person.emergency_contact,
        family_role: person.family_role
      });
    }
  }, [person]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim() || !id) return;

    try {
      await updatePerson(id, formData);
      toast({
        title: "Personne modifiée",
        description: "Les modifications ont été enregistrées.",
      });
      navigate('/persons');
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de modifier la personne. Veuillez réessayer.",
        variant: "destructive",
      });
    }
  };

  if (!family) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeader title="Modifier la personne" backPath="/persons" />
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-muted-foreground text-center">
            Créez d'abord une famille pour pouvoir modifier des personnes.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeader title="Modifier la personne" backPath="/persons" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeader title="Modifier la personne" backPath="/persons" />
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-muted-foreground text-center">Personne introuvable.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader title="Modifier la personne" backPath="/persons" />

      <div className="flex-1 p-4 pb-32">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="first_name" className="text-sm font-medium">
                Prénom *
              </label>
              <Input
                id="first_name"
                placeholder="Prénom"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="last_name" className="text-sm font-medium">
                Nom *
              </label>
              <Input
                id="last_name"
                placeholder="Nom de famille"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Catégorie *</label>
            <Select value={formData.category} onValueChange={(value: CreatePersonData['category']) => setFormData({ ...formData, category: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir une catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="family">👨‍👩‍👧‍👦 Famille</SelectItem>
                <SelectItem value="extended_family">👨‍👩‍👧‍👦 Famille étendue</SelectItem>
                <SelectItem value="friends">👫 Amis</SelectItem>
                <SelectItem value="medical_other">👨‍⚕️ Médical/Autres</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.category === 'family' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Rôle familial</label>
              <Select value={formData.family_role || ''} onValueChange={(value: CreatePersonData['family_role']) => setFormData({ ...formData, family_role: value || undefined })}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="spouse">Conjoint(e)</SelectItem>
                  <SelectItem value="child">Enfant</SelectItem>
                  <SelectItem value="grandparent">Grand-parent</SelectItem>
                  <SelectItem value="guardian">Tuteur/Tutrice</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="phone_primary" className="text-sm font-medium">
                Téléphone principal
              </label>
              <Input
                id="phone_primary"
                type="tel"
                placeholder="01 23 45 67 89"
                value={formData.phone_primary}
                onChange={(e) => setFormData({ ...formData, phone_primary: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="phone_primary" className="text-sm font-medium">
                Téléphone
              </label>
              <Input
                id="phone_primary"
                type="tel"
                placeholder="+33 6 12 34 56 78"
                value={formData.phone_primary}
                onChange={(e) => setFormData({ ...formData, phone_primary: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="email@exemple.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          {formData.category === 'medical_other' && (
            <div className="space-y-2">
              <label htmlFor="medical_speciality" className="text-sm font-medium">
                Spécialité médicale
              </label>
              <Input
                id="medical_speciality"
                placeholder="Cardiologue, Pédiatre, etc."
                value={formData.medical_speciality}
                onChange={(e) => setFormData({ ...formData, medical_speciality: e.target.value })}
              />
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="address" className="text-sm font-medium">
              Adresse
            </label>
            <Textarea
              id="address"
              placeholder="Adresse complète"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="notes" className="text-sm font-medium">
              Notes
            </label>
            <Textarea
              id="notes"
              placeholder="Notes ou informations supplémentaires"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="emergency_contact"
              checked={formData.emergency_contact}
              onCheckedChange={(checked) => setFormData({ ...formData, emergency_contact: !!checked })}
            />
            <Label htmlFor="emergency_contact">Contact d'urgence</Label>
          </div>
        </form>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 border-t bg-background space-y-3 z-50">
        <Button 
          onClick={handleSubmit}
          disabled={loading || !formData.first_name.trim() || !formData.last_name.trim()}
          className="w-full"
          variant={primaryVariant as any}
        >
          {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
        <Button 
          variant="cancel-hover" 
          onClick={() => navigate('/persons')}
          className="w-full"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
}